import { useState, useEffect, useRef } from 'react'
import { useLocation, useSearchParams, Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { ROUTES } from '@/constants/routes'
import { FiChevronDown, FiPlus } from 'react-icons/fi'
import {
  getUsers,
  getCurrentUser as getCurrentUserApi,
  createUser,
  updateUser,
  deleteUser,
  uploadUserAvatar,
  getAvatarUrl,
  ROLE_LABELS,
  type User,
  type UserRole,
  type CreateUserPayload,
  type UpdateUserPayload,
} from '@/services/api/users'
import { getUser, setUser } from '@/utils/auth'
import { hasPermission } from '@/constants/permissions'
import type { UserRole as PermissionUserRole } from '@/types'
import {
  getXeroStatus,
  getXeroAuthUrl,
  disconnectXero,
  syncXeroInvoices,
  syncXeroPurchases,
  syncXeroPayments,
  syncXeroContacts,
  syncXeroProductsServices,
  syncXeroAll,
  getXeroBusinessSnapshot,
  pushXeroOperationalCosts,
  getXeroStockSummary,
  type XeroStatus,
  type StockAndCostSummaryResponse,
} from '@/services/api/xero'
import {
  getGmailStatus,
  getGmailAuthUrl,
  disconnectGmail,
  runStockImportFromGmail,
  type GmailStatus,
  type StockReconImportResult,
} from '@/services/api/gmail'
import {
  getSalesforceStatus,
  getSalesforceAuthUrl,
  disconnectSalesforce,
  type SalesforceStatus,
} from '@/services/api/salesforce'
import SalesforceSyncButton from '@/components/common/SalesforceSyncButton'
import {
  startSalesforceSync,
  subscribeSalesforceSync,
  watchSalesforceSyncInBackground,
  type SalesforceSyncState,
} from '@/services/salesforceSync'

type TabKey = 'general' | 'integrations' | 'notifications' | 'user-management'

const tabToPath: Record<TabKey, string> = {
  general: ROUTES.SETTINGS_GENERAL,
  integrations: ROUTES.SETTINGS_INTEGRATIONS,
  notifications: ROUTES.SETTINGS_NOTIFICATIONS,
  'user-management': ROUTES.SETTINGS_USER_MANAGEMENT,
}

type GeneralSettingsState = {
  companyName: string
  timezone: string
  dateFormat: string
  darkMode: boolean
}

type NotificationSettingsState = {
  emailNotifications: boolean
  smsNotifications: boolean
  shipmentStatusChanges: boolean
  documentReminders: boolean
  inventoryAlerts: boolean
  paymentNotifications: boolean
}

const getSettingsStorageKey = (userIdOrEmail: string | undefined, section: 'general' | 'notifications') =>
  `settings:${section}:${userIdOrEmail || 'anonymous'}`

const Settings = () => {
  const location = useLocation()

  // Initialize activeTab based on current route
  const getInitialTab = (): TabKey => {
    if (location.pathname === ROUTES.SETTINGS_INTEGRATIONS) return 'integrations'
    if (location.pathname === ROUTES.SETTINGS_NOTIFICATIONS) return 'notifications'
    if (location.pathname === ROUTES.SETTINGS_USER_MANAGEMENT) return 'user-management'
    return 'general'
  }

  const [activeTab, setActiveTab] = useState<TabKey>(getInitialTab())

  // Keep activeTab in sync with URL (e.g. when navigating to /settings/integrations via link)
  useEffect(() => {
    setActiveTab(getInitialTab())
  }, [location.pathname])
  const [companyName, setCompanyName] = useState('')
  const [timezone, setTimezone] = useState('UTC (Coordinated Universal Time)')
  const [dateFormat, setDateFormat] = useState('MM/DD/YYYY')
  const [darkMode, setDarkMode] = useState(false)
  
  // Integrations state (shipping lines are configured in backend .env; no UI toggles/API fields)
  const [xeroEnabled, setXeroEnabled] = useState(() => {
    try {
      return typeof sessionStorage !== 'undefined' && sessionStorage.getItem('xeroConnected') === 'true'
    } catch (_) {
      return false
    }
  })
  const [xeroStatus, setXeroStatus] = useState<XeroStatus | null>(() => {
    try {
      if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('xeroConnected') === 'true') {
        return { connected: true }
      }
    } catch (_) {}
    return null
  })
  const [xeroLoading, setXeroLoading] = useState(false)
  const [xeroSyncLoading, setXeroSyncLoading] = useState<string | null>(null)
  const [xeroSyncAllLoading, setXeroSyncAllLoading] = useState(false)
  const [xeroSnapshot, setXeroSnapshot] = useState<{ organisationName: string; tenantName: string; contactCounts?: { customers: number; suppliers: number } } | null>(null)
  const [xeroSnapshotLoading, setXeroSnapshotLoading] = useState(false)
  const [xeroPushLoading, setXeroPushLoading] = useState(false)
  const [xeroDisconnectLoading, setXeroDisconnectLoading] = useState(false)
  const [xeroStockSummary, setXeroStockSummary] = useState<StockAndCostSummaryResponse | null>(null)
  const [xeroStockSummaryLoading, setXeroStockSummaryLoading] = useState(false)
  const [gmailStatus, setGmailStatus] = useState<GmailStatus | null>(null)
  const [gmailLoading, setGmailLoading] = useState(false)
  const [gmailDisconnectLoading, setGmailDisconnectLoading] = useState(false)
  const [stockImportLoading, setStockImportLoading] = useState(false)
  const [stockImportResult, setStockImportResult] = useState<StockReconImportResult | null>(null)
  const [stockImportMessage, setStockImportMessage] = useState<string | null>(null)
  const [salesforceStatus, setSalesforceStatus] = useState<SalesforceStatus | null>(null)
  const [salesforceLoading, setSalesforceLoading] = useState(false)
  const [salesforceDisconnectLoading, setSalesforceDisconnectLoading] = useState(false)
  const [salesforceSyncJob, setSalesforceSyncJob] = useState<SalesforceSyncState>({
    status: 'idle',
    fetched: 0,
    processed: 0,
    upserted: 0,
    since: '2026-04-01',
    percent: 0,
  })
  const [searchParams, setSearchParams] = useSearchParams()
  const xeroConnectToastShownRef = useRef(false)
  const gmailConnectToastShownRef = useRef(false)
  const salesforceConnectToastShownRef = useRef(false)
  const [savingGeneral, setSavingGeneral] = useState(false)
  const [savingNotifications, setSavingNotifications] = useState(false)

  // User Management state (must be before useEffect that uses them)
  const currentUser = getUser()
  const settingsUserKey = currentUser?.id || currentUser?.email
  const isAdmin = currentUser ? hasPermission(currentUser.role as PermissionUserRole, 'users:view') : false
  const isCurrentUserRecord = (user: { id: string; email: string }) => {
    if (!currentUser) return false
    if (currentUser.id && currentUser.id === user.id) return true
    return currentUser.email.toLowerCase() === user.email.toLowerCase()
  }
  const [usersList, setUsersList] = useState<User[]>([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [userManagementError, setUserManagementError] = useState<string | null>(null)
  const [addUserOpen, setAddUserOpen] = useState(false)
  const [editUserOpen, setEditUserOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [userForm, setUserForm] = useState<{ name: string; email: string; password: string; role: UserRole }>({
    name: '',
    email: '',
    password: '',
    role: 'operations_manager',
  })
  const [userFormSubmitting, setUserFormSubmitting] = useState(false)
  const [avatarUploading, setAvatarUploading] = useState(false)

  // User Management: fetch users when on tab (admin only)
  useEffect(() => {
    if (activeTab !== 'user-management' || !isAdmin) return
    setUserManagementError(null)
    setUsersLoading(true)
    getUsers({ page: 1, limit: 100 })
      .then((res) => setUsersList(res.users))
      .catch((e: any) => {
        const msg = e?.response?.data?.error?.message ?? e?.message ?? 'Failed to load users'
        setUserManagementError(msg)
        toast.error(msg)
      })
      .finally(() => setUsersLoading(false))
  }, [activeTab, isAdmin])

  // Xero: fetch status when on integrations tab. Backend /xero/status does not require auth.
  // Do NOT set connected: false on API failure – connection only changes when user clicks Disconnect or API returns connected: false.
  useEffect(() => {
    if (activeTab !== 'integrations') return
    setXeroLoading(true)
    getXeroStatus()
      .then((s) => {
        setXeroStatus(s)
        try {
          if (s?.connected) {
            setXeroEnabled(true)
            sessionStorage.setItem('xeroConnected', 'true')
          } else {
            // Only update to "connected" when backend says so; never auto-disconnect (only user Disconnect clears this)
            const wasConnected = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('xeroConnected') === 'true'
            if (!wasConnected) setXeroEnabled(false)
          }
        } catch (_) {}
      })
      .catch(() => {
        // Keep previous status on error (network/backend) – don't assume disconnected
      })
      .finally(() => setXeroLoading(false))
  }, [activeTab])

  // Gmail: fetch status when on integrations tab
  useEffect(() => {
    if (activeTab !== 'integrations') return
    setGmailLoading(true)
    getGmailStatus()
      .then((s) => setGmailStatus(s))
      .catch(() => {
        // Keep previous status on error
      })
      .finally(() => setGmailLoading(false))
  }, [activeTab])

  useEffect(() => {
    if (activeTab !== 'integrations') return
    setSalesforceLoading(true)
    getSalesforceStatus()
      .then((s) => setSalesforceStatus(s))
      .catch(() => {})
      .finally(() => setSalesforceLoading(false))
  }, [activeTab])

  // Gmail: show success toast when returning from OAuth callback
  useEffect(() => {
    const gmailParam = searchParams.get('gmail')
    if (gmailParam !== 'connected') return
    if (gmailConnectToastShownRef.current) return
    gmailConnectToastShownRef.current = true

    const connectedEmail = searchParams.get('email')
    toast.success(
      connectedEmail
        ? `Gmail connected successfully (${connectedEmail}).`
        : 'Gmail connected successfully.'
    )
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('gmail')
      next.delete('email')
      return next
    }, { replace: true })

    getGmailStatus()
      .then((s) => setGmailStatus(s))
      .catch(() => {})
  }, [searchParams, setSearchParams])

  useEffect(() => {
    const sfParam = searchParams.get('salesforce')
    if (sfParam === 'error') {
      toast.error(
        searchParams.get('message') === 'missing_code'
          ? 'Salesforce did not return an authorization code. Check SALESFORCE_REDIRECT_URI on the Connected App.'
          : 'Salesforce connection failed. Re-try Connect Salesforce.'
      )
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.delete('salesforce')
        next.delete('message')
        return next
      }, { replace: true })
      return
    }
    if (sfParam !== 'connected') return
    if (salesforceConnectToastShownRef.current) return
    salesforceConnectToastShownRef.current = true
    const username = searchParams.get('username')
    toast.success(username ? `Salesforce connected (${username}).` : 'Salesforce connected.')
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('salesforce')
      next.delete('username')
      return next
    }, { replace: true })
    getSalesforceStatus()
      .then((s) => setSalesforceStatus(s))
      .catch(() => {})
  }, [searchParams, setSearchParams])

  useEffect(() => {
    watchSalesforceSyncInBackground()
    return subscribeSalesforceSync(setSalesforceSyncJob)
  }, [])

  // Xero: show success or error toast when returning from OAuth callback; fetch status on success
  useEffect(() => {
    const xeroParam = searchParams.get('xero')
    const message = searchParams.get('message')

    if (xeroParam === 'error') {
      if (message === 'missing_code') {
        toast.error(
          'Xero did not return an authorization code. Set XERO_REDIRECT_URI in the backend to your backend callback URL (e.g. https://your-api.com/api/xero/callback) and ensure it matches the Redirect URI in the Xero app config.'
        )
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev)
          next.delete('xero')
          next.delete('message')
          return next
        }, { replace: true })
      }
      return
    }

    if (xeroParam !== 'connected') return
    // Guard against React StrictMode double-invoking effects in development
    if (xeroConnectToastShownRef.current) return
    xeroConnectToastShownRef.current = true
    toast.success('Xero connected successfully.')
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('xero')
      return next
    }, { replace: true })
    const fetchStatus = () =>
      getXeroStatus().then((s) => {
        setXeroStatus(s)
        try {
          if (s?.connected) {
            setXeroEnabled(true)
            sessionStorage.setItem('xeroConnected', 'true')
          }
          // Don't auto-disconnect when backend returns false; only user Disconnect clears state
        } catch (_) {}
      })
    fetchStatus().catch(() => {
      // Retry once after short delay (backend may still be writing connection)
      setTimeout(() => fetchStatus().catch(() => {}), 800)
    })
  }, [searchParams, setSearchParams])

  const handleConnectXero = async () => {
    // Don't redirect to Xero if already connected (avoids opening Xero login again)
    if (xeroStatus?.connected) {
      toast.info('Xero is already connected. Use the sync buttons to refresh data.')
      return
    }
    try {
      const url = await getXeroAuthUrl()
      if (url) window.location.href = url
      else toast.error('Could not get Xero authorization URL.')
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Failed to connect to Xero.')
    }
  }

  const handleConnectGmail = async () => {
    if (gmailStatus?.connected) {
      toast.info('Gmail is already connected. Use Load Stock Levels to import the latest Stock Recon email.')
      return
    }
    try {
      const url = await getGmailAuthUrl()
      if (url) window.location.href = url
      else toast.error('Could not get Gmail authorization URL.')
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Failed to connect to Gmail.')
    }
  }

  const handleConnectSalesforce = async () => {
    if (salesforceStatus?.connected) {
      toast.info('Salesforce is already connected. Use Sync Orders to refresh.')
      return
    }
    try {
      const url = await getSalesforceAuthUrl()
      if (url) window.location.href = url
      else toast.error('Could not get Salesforce authorization URL.')
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Failed to connect to Salesforce.')
    }
  }

  const handleLoadStockLevels = async () => {
    if (!gmailStatus?.connected) {
      toast.error('Connect Gmail first to load stock levels from Stock Recon emails.')
      return
    }

    setStockImportLoading(true)
    setStockImportResult(null)
    setStockImportMessage(null)
    const toastId = toast.loading('Loading stock levels from Gmail...', { autoClose: false })

    try {
      const { result, message } = await runStockImportFromGmail()
      setStockImportResult(result)
      setStockImportMessage(message)

      if (result.imported) {
        sessionStorage.removeItem('dashboard:stats:cache')
        sessionStorage.removeItem('dashboard:inventory-status:5')
        toast.update(toastId, {
          render: message || 'Stock levels updated successfully.',
          type: 'success',
          isLoading: false,
          autoClose: 5000,
        })
      } else if (result.reason === 'already_imported') {
        toast.update(toastId, {
          render: message || 'Latest Stock Recon email was already imported.',
          type: 'info',
          isLoading: false,
          autoClose: 5000,
        })
      } else if (result.error) {
        toast.update(toastId, {
          render: result.error,
          type: 'error',
          isLoading: false,
          autoClose: 8000,
        })
      } else {
        toast.update(toastId, {
          render: message || 'No new stock data to import.',
          type: 'warning',
          isLoading: false,
          autoClose: 6000,
        })
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.response?.data?.error?.message || e?.message || 'Failed to load stock levels.'
      toast.update(toastId, {
        render: msg,
        type: 'error',
        isLoading: false,
        autoClose: 8000,
      })
    } finally {
      setStockImportLoading(false)
    }
  }

  const handleSyncXero = async (type: 'invoices' | 'purchases' | 'payments' | 'contacts' | 'products-services') => {
    setXeroSyncLoading(type)
    const label =
      type === 'products-services'
        ? 'Products & Services'
        : type.charAt(0).toUpperCase() + type.slice(1)
    const toastId = toast.loading(`Syncing ${label} from Xero...`, { autoClose: false })
    try {
      let res: any
      if (type === 'invoices') res = await syncXeroInvoices()
      else if (type === 'purchases') res = await syncXeroPurchases()
      else if (type === 'payments') res = await syncXeroPayments()
      else if (type === 'contacts') res = await syncXeroContacts()
      else res = await syncXeroProductsServices()
      toast.update(toastId, {
        render: res?.message || `${label} sync completed.`,
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      })
      if (type === 'contacts' && (res as any)?.customers != null && (res as any)?.suppliers != null && xeroSnapshot) {
        setXeroSnapshot((s) => s ? { ...s, contactCounts: { customers: (res as any).customers, suppliers: (res as any).suppliers } } : null)
      }
    } catch (e: any) {
      const status = e?.response?.status
      const msg = e?.response?.data?.error?.message ?? e?.message ?? `Failed to sync ${type}.`
      const isXeroAuthFailure = status === 401 || msg?.includes?.('Xero authentication failed') || msg?.includes?.('AuthorizationUnsuccessful')
      if (isXeroAuthFailure) {
        setXeroStatus((s) => (s ? { ...s, connected: false } : null))
        try { sessionStorage.removeItem('xeroConnected') } catch (_) {}
        toast.update(toastId, {
          render: 'Xero connection expired. Please click "Disconnect Xero" then connect again to re-authorize.',
          type: 'error',
          isLoading: false,
          autoClose: 5000,
        })
      } else {
        toast.update(toastId, {
          render: msg,
          type: 'error',
          isLoading: false,
          autoClose: 5000,
        })
      }
    } finally {
      setXeroSyncLoading(null)
    }
  }

  const handleSyncAllXero = async () => {
    setXeroSyncAllLoading(true)
    const toastId = toast.loading('Syncing all Xero data...', { autoClose: false })
    const types: Array<'invoices' | 'purchases' | 'payments' | 'contacts' | 'products-services'> = [
      'invoices',
      'purchases',
      'payments',
      'contacts',
      'products-services',
    ]
    try {
      const data = await syncXeroAll()
      const results = types.map((type) => {
        const key = type === 'products-services' ? 'productsServices' : type
        const r = data[key as keyof typeof data]
        return { type, ok: r?.ok ?? false, message: r?.error }
      })
      if (data.contacts?.ok && data.contacts?.customers != null && data.contacts?.suppliers != null && xeroSnapshot) {
        setXeroSnapshot((s) => s ? { ...s, contactCounts: { customers: data.contacts!.customers!, suppliers: data.contacts!.suppliers! } } : null)
      }
      results.forEach((r) => {
        if (!r.ok && r.message) toast.error(`Sync ${r.type}: ${r.message}`)
      })
      const okCount = results.filter((r) => r.ok).length
      if (okCount === types.length) {
        toast.update(toastId, {
          render: 'All Xero data synced successfully.',
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        })
      } else if (okCount > 0) {
        toast.update(toastId, {
          render: `Synced ${okCount} of ${types.length}. Check errors above.`,
          type: 'warning',
          isLoading: false,
          autoClose: 5000,
        })
      } else {
        toast.update(toastId, {
          render: 'Xero sync failed. Please check the errors shown.',
          type: 'error',
          isLoading: false,
          autoClose: 5000,
        })
      }
      const isXeroAuthFailure = results.some((r) => !r.ok && (r.message?.includes?.('Xero authentication failed') ?? r.message?.includes?.('AuthorizationUnsuccessful')))
      if (isXeroAuthFailure) {
        setXeroStatus((s) => (s ? { ...s, connected: false } : null))
        try { sessionStorage.removeItem('xeroConnected') } catch (_) {}
        toast.error('Xero connection expired. Please click "Disconnect Xero" then connect again to re-authorize.')
      }
    } catch (e: any) {
      const msg = e?.response?.data?.error?.message ?? e?.message ?? 'Failed to sync Xero data.'
      const isXeroAuthFailure = e?.response?.status === 401 || msg?.includes?.('Xero authentication failed') || msg?.includes?.('AuthorizationUnsuccessful')
      toast.update(toastId, {
        render: msg,
        type: 'error',
        isLoading: false,
        autoClose: 5000,
      })
      if (isXeroAuthFailure) {
        setXeroStatus((s) => (s ? { ...s, connected: false } : null))
        try { sessionStorage.removeItem('xeroConnected') } catch (_) {}
      }
    } finally {
      setXeroSyncAllLoading(false)
    }
  }

  const handleLoadXeroSnapshot = async () => {
    setXeroSnapshotLoading(true)
    try {
      const res = await getXeroBusinessSnapshot()
      setXeroSnapshot({
        organisationName: res.organisationName,
        tenantName: res.tenantName,
        contactCounts: res.contactCounts,
      })
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Failed to load business snapshot.')
    } finally {
      setXeroSnapshotLoading(false)
    }
  }

  // Notifications state
  const [emailNotifications, setEmailNotifications] = useState(true)
  const [smsNotifications, setSmsNotifications] = useState(false)
  const [shipmentStatusChanges, setShipmentStatusChanges] = useState(true)
  const [documentReminders, setDocumentReminders] = useState(false)
  const [inventoryAlerts, setInventoryAlerts] = useState(true)
  const [paymentNotifications, setPaymentNotifications] = useState(false)

  // Load persisted settings for current user/session.
  useEffect(() => {
    try {
      const generalRaw = localStorage.getItem(getSettingsStorageKey(settingsUserKey, 'general'))
      if (generalRaw) {
        const parsed = JSON.parse(generalRaw) as Partial<GeneralSettingsState>
        if (typeof parsed.companyName === 'string') setCompanyName(parsed.companyName)
        if (typeof parsed.timezone === 'string') setTimezone(parsed.timezone)
        if (typeof parsed.dateFormat === 'string') setDateFormat(parsed.dateFormat)
        if (typeof parsed.darkMode === 'boolean') setDarkMode(parsed.darkMode)
      }

      const notificationRaw = localStorage.getItem(getSettingsStorageKey(settingsUserKey, 'notifications'))
      if (notificationRaw) {
        const parsed = JSON.parse(notificationRaw) as Partial<NotificationSettingsState>
        if (typeof parsed.emailNotifications === 'boolean') setEmailNotifications(parsed.emailNotifications)
        if (typeof parsed.smsNotifications === 'boolean') setSmsNotifications(parsed.smsNotifications)
        if (typeof parsed.shipmentStatusChanges === 'boolean') setShipmentStatusChanges(parsed.shipmentStatusChanges)
        if (typeof parsed.documentReminders === 'boolean') setDocumentReminders(parsed.documentReminders)
        if (typeof parsed.inventoryAlerts === 'boolean') setInventoryAlerts(parsed.inventoryAlerts)
        if (typeof parsed.paymentNotifications === 'boolean') setPaymentNotifications(parsed.paymentNotifications)
      }
    } catch (_) {
      // Ignore malformed local settings and keep defaults.
    }
  }, [settingsUserKey])

  const handleSaveGeneralSettings = () => {
    setSavingGeneral(true)
    try {
      const payload: GeneralSettingsState = { companyName, timezone, dateFormat, darkMode }
      localStorage.setItem(getSettingsStorageKey(settingsUserKey, 'general'), JSON.stringify(payload))
      toast.success('General settings saved.')
    } catch (_) {
      toast.error('Failed to save general settings.')
    } finally {
      setSavingGeneral(false)
    }
  }

  const handleSaveNotificationSettings = () => {
    setSavingNotifications(true)
    try {
      const payload: NotificationSettingsState = {
        emailNotifications,
        smsNotifications,
        shipmentStatusChanges,
        documentReminders,
        inventoryAlerts,
        paymentNotifications,
      }
      localStorage.setItem(getSettingsStorageKey(settingsUserKey, 'notifications'), JSON.stringify(payload))
      toast.success('Notification settings saved.')
    } catch (_) {
      toast.error('Failed to save notification settings.')
    } finally {
      setSavingNotifications(false)
    }
  }

  const rolesList = [
    { id: 'admin', title: 'Administrator', description: 'Full access to all system features and settings.' },
    { id: 'operations_manager', title: 'Operations Manager', description: 'Access to shipments, documents, and reporting features.' },
    { id: 'inventory_manager', title: 'Inventory Manager', description: 'Access to inventory management features only.' },
    { id: 'clearing_officer', title: 'Clearing Officer', description: 'Access to clearing and related documents.' },
    { id: 'finance_team', title: 'Finance Team', description: 'Access to payments, vendors, and finance reports.' },
  ]

  const tabs = [
    { label: 'General', key: 'general' as TabKey },
    { label: 'Integrations', key: 'integrations' as TabKey },
    { label: 'Notifications', key: 'notifications' as TabKey },
    { label: 'User Management', key: 'user-management' as TabKey },
  ]

  const handleTabClick = (tabKey: TabKey) => {
    setActiveTab(tabKey)
  }

  const getTabContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <div className="p-6">
            <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
              {/* Left Section - Header */}
              <div className="flex-shrink-0" style={{ minWidth: '280px' }}>
                <h2
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 600,
                    fontStyle: 'normal',
                    fontSize: '20px',
                    lineHeight: '32px',
                    letterSpacing: '0%',
                    color: '#212126',
                    marginBottom: '8px'
                  }}
                >
                  General Settings
                </h2>
                <p
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '22px',
                    letterSpacing: '0%',
                    color: '#212126'
                  }}
                >
                  Configure basic settings for your LogistiTrack CRM
                </p>
              </div>

              {/* Right Section - Settings Form Card */}
              <div className="flex-1">
                <div
                  className="bg-white rounded-lg border"
                  style={{
                    border: '1px solid #E9E9EA',
                    borderRadius: '8px',
                    padding: '24px'
                  }}
                >
                  <div className="space-y-6">
                    {/* Company Name and Timezone on same line */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Company Name */}
                      <div>
                        <label
                          htmlFor="companyName"
                          style={{
                            fontFamily: 'Lexend Deca',
                            fontWeight: 500,
                            fontStyle: 'normal',
                            fontSize: '14px',
                            lineHeight: '22px',
                            letterSpacing: '0px',
                            color: '#69697A',
                            display: 'block',
                            marginBottom: '8px'
                          }}
                        >
                          Company Name
                        </label>
                        <input
                          id="companyName"
                          type="text"
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                          placeholder="Enter Your Import/Export Company"
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          style={{
                            fontFamily: 'Lexend Deca',
                            fontWeight: 300,
                            fontSize: '12px',
                            lineHeight: '20px',
                            letterSpacing: '0px',
                            color: '#212126',
                            height: '40px',
                            border: '1px solid #E9E9EA',
                            borderRadius: '6px',
                            paddingLeft: '16px',
                            paddingRight: '16px'
                          }}
                        />
                        <style>{`
                          #companyName::placeholder {
                            font-family: 'Lexend Deca';
                            font-weight: 300;
                            font-style: normal;
                            font-size: 12px;
                            line-height: 20px;
                            letter-spacing: 0px;
                            color: #69697A;
                          }
                        `}</style>
                      </div>

                      {/* Timezone */}
                      <div>
                        <label
                          htmlFor="timezone"
                          style={{
                            fontFamily: 'Lexend Deca',
                            fontWeight: 500,
                            fontStyle: 'normal',
                            fontSize: '14px',
                            lineHeight: '22px',
                            letterSpacing: '0px',
                            color: '#69697A',
                            display: 'block',
                            marginBottom: '8px'
                          }}
                        >
                          Timezone
                        </label>
                        <div className="relative">
                          <select
                            id="timezone"
                            value={timezone}
                            onChange={(e) => setTimezone(e.target.value)}
                            className="w-full px-4 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none"
                            style={{
                              fontFamily: 'Lexend Deca',
                              fontWeight: 300,
                              fontSize: '12px',
                              lineHeight: '20px',
                              letterSpacing: '0px',
                              color: '#69697A',
                              height: '40px',
                              border: '1px solid #E9E9EA',
                              borderRadius: '6px',
                              paddingLeft: '16px',
                              paddingRight: '40px'
                            }}
                          >
                            <option>UTC (Coordinated Universal Time)</option>
                            <option>EST (Eastern Standard Time)</option>
                            <option>PST (Pacific Standard Time)</option>
                            <option>GMT (Greenwich Mean Time)</option>
                          </select>
                          <FiChevronDown
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                            style={{
                              width: '16px',
                              height: '16px',
                              color: '#69697A'
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Date Format */}
                    <div className="w-full md:w-1/2">
                      <label
                        htmlFor="dateFormat"
                        style={{
                          fontFamily: 'Lexend Deca',
                          fontWeight: 500,
                          fontStyle: 'normal',
                          fontSize: '14px',
                          lineHeight: '22px',
                          letterSpacing: '0px',
                          color: '#69697A',
                          display: 'block',
                          marginBottom: '8px'
                        }}
                      >
                        Date Format
                      </label>
                      <div className="relative">
                        <input
                          id="dateFormat"
                          type="text"
                          value={dateFormat}
                          onChange={(e) => setDateFormat(e.target.value)}
                          className="w-full px-4 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          style={{
                            fontFamily: 'Lexend Deca',
                            fontWeight: 300,
                            fontSize: '12px',
                            lineHeight: '20px',
                            letterSpacing: '0px',
                            color: '#69697A',
                            height: '40px',
                            border: '1px solid #E9E9EA',
                            borderRadius: '6px',
                            paddingLeft: '16px',
                            paddingRight: '40px'
                          }}
                        />
                        <img
                          src="/images/calendar.svg"
                          alt="Calendar"
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                          style={{
                            width: '18px',
                            height: '18px'
                          }}
                        />
                      </div>
                    </div>

                    {/* Dark Mode */}
                    <div className="w-full md:w-1/2">
                      <label
                        htmlFor="darkMode"
                        style={{
                          fontFamily: 'Lexend Deca',
                          fontWeight: 500,
                          fontStyle: 'normal',
                          fontSize: '14px',
                          lineHeight: '22px',
                          letterSpacing: '0px',
                          color: '#69697A',
                          display: 'block',
                          marginBottom: '8px'
                        }}
                      >
                        Dark Mode
                      </label>
                      <div className="relative" style={{ height: '40px', display: 'flex', alignItems: 'center' }}>
                        <p
                          style={{
                            fontFamily: 'Lexend Deca',
                            fontWeight: 400,
                            fontStyle: 'normal',
                            fontSize: '12px',
                            lineHeight: '16px',
                            letterSpacing: '0%',
                            color: '#69697A',
                            margin: 0,
                            flex: 1
                          }}
                        >
                          Enable dark mode interface
                        </p>
                        <button
                          id="darkMode"
                          onClick={() => setDarkMode(!darkMode)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                            darkMode ? 'bg-gray-700' : 'bg-gray-300'
                          }`}
                          style={{
                            height: '24px',
                            width: '44px',
                            borderRadius: '12px',
                            backgroundColor: darkMode ? '#212126' : '#E9E9EA',
                            transition: 'background-color 0.2s ease',
                            flexShrink: 0,
                            position: 'absolute',
                            right: 0,
                            top: '50%',
                            transform: 'translateY(-50%)'
                          }}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                              darkMode ? 'translate-x-6' : 'translate-x-1'
                            }`}
                            style={{
                              height: '20px',
                              width: '20px',
                              borderRadius: '50%',
                              backgroundColor: '#FFFFFF',
                              transform: darkMode ? 'translateX(20px)' : 'translateX(2px)',
                              transition: 'transform 0.2s ease',
                              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                            }}
                          />
                        </button>
                      </div>
                    </div>

                    {/* Save Changes Button */}
                    <div className="pt-4">
                      <button
                        onClick={handleSaveGeneralSettings}
                        disabled={savingGeneral}
                        className="w-full sm:w-auto px-6 py-2 text-white font-medium rounded-lg hover:opacity-90 transition-opacity"
                        style={{
                          fontFamily: 'Lexend Deca',
                          fontWeight: 400,
                          fontSize: '14px',
                          lineHeight: '20px',
                          color: '#FFFFFF',
                          backgroundColor: '#212126',
                          borderRadius: '6px',
                          paddingTop: '10px',
                          paddingBottom: '10px',
                          paddingLeft: '24px',
                          paddingRight: '24px',
                          height: '40px',
                          opacity: savingGeneral ? 0.7 : 1,
                          cursor: savingGeneral ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {savingGeneral ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      case 'integrations':
        return (
          <div className="p-6">
            <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
              {/* Left Section - Header */}
              <div className="flex-shrink-0" style={{ minWidth: '280px' }}>
                <h2
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 600,
                    fontStyle: 'normal',
                    fontSize: '20px',
                    lineHeight: '32px',
                    letterSpacing: '0%',
                    color: '#212126',
                    marginBottom: '8px'
                  }}
                >
                  API Integrations
                </h2>
                <p
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '22px',
                    letterSpacing: '0%',
                    color: '#212126'
                  }}
                >
                  Configure integrations with shipping lines and other services.
                </p>
              </div>

              {/* Right Section - Integrations Form */}
              <div className="flex-1">
                <div className="space-y-6">
                  {/* Shipping Lines Section */}
                  <div>
                    <h3
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 500,
                        fontStyle: 'normal',
                        fontSize: '14px',
                        lineHeight: '22px',
                        letterSpacing: '0px',
                        color: '#69697A',
                        marginBottom: '16px'
                      }}
                    >
                      Shipping Lines
                    </h3>
                    <p
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        fontSize: '12px',
                        lineHeight: '18px',
                        color: '#69697A',
                        marginBottom: '12px'
                      }}
                    >
                      Carrier tracking is configured on the server. Set credentials in the backend .env; tracking runs automatically for active shipments.
                    </p>
                    <div className="space-y-4">
                      {/* Hapag Lloyd */}
                      <div className="bg-white rounded-lg border" style={{ border: '1px solid #E9E9EA', borderRadius: '8px', padding: '16px' }}>
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0" style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img
                              src="/images/Hapag-Lloyd.svg"
                              alt="Hapag Lloyd"
                              style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                            />
                          </div>
                          <div className="flex-1">
                            <h4
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: '22px',
                                color: '#212126',
                                marginBottom: '4px'
                              }}
                            >
                              Hapag Lloyd
                            </h4>
                            <p
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontWeight: 400,
                                fontSize: '12px',
                                lineHeight: '16px',
                                color: '#69697A',
                                marginBottom: '4px'
                              }}
                            >
                              DCSA Track & Trace API v2.2.4 — auth via X-IBM-Client-Id / X-IBM-Client-Secret headers (no OAuth).
                            </p>
                            <p
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontWeight: 400,
                                fontSize: '11px',
                                lineHeight: '16px',
                                color: '#69697A',
                                marginBottom: '4px'
                              }}
                            >
                              Configure in backend .env: HAPAG_CLIENT_ID, HAPAG_CLIENT_SECRET, HAPAG_BASE_URL (optional).
                            </p>
                            <a
                              href="https://api-portal.hlag.com/products/portfolio/events-tracing-for-web-api-product-d73213?version=2"
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontWeight: 400,
                                fontSize: '11px',
                                lineHeight: '16px',
                                color: '#0B57D0',
                                textDecoration: 'underline',
                              }}
                            >
                              API documentation: Hapag-Lloyd Events Tracing (DCSA)
                            </a>
                          </div>
                        </div>
                      </div>

                      {/* MSC */}
                      <div className="bg-white rounded-lg border" style={{ border: '1px solid #E9E9EA', borderRadius: '8px', padding: '16px' }}>
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0" style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img
                              src="/images/msc.svg"
                              alt="MSC"
                              style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                            />
                          </div>
                          <div className="flex-1">
                            <h4
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: '22px',
                                color: '#212126',
                                marginBottom: '4px'
                              }}
                            >
                              MSC - Mediterranean Shipping Company
                            </h4>
                            <p
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontWeight: 400,
                                fontSize: '12px',
                                lineHeight: '16px',
                                color: '#69697A'
                              }}
                            >
                              Container tracking and scheduling. Integration pending; configure via backend when available.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Maersk */}
                      <div className="bg-white rounded-lg border" style={{ border: '1px solid #E9E9EA', borderRadius: '8px', padding: '16px' }}>
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0" style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img
                              src="/images/Maersk.svg"
                              alt="Maersk"
                              style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                            />
                          </div>
                          <div className="flex-1">
                            <h4
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: '22px',
                                color: '#212126',
                                marginBottom: '4px'
                              }}
                            >
                              Maersk
                            </h4>
                            <p
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontWeight: 400,
                                fontSize: '12px',
                                lineHeight: '16px',
                                color: '#69697A',
                                marginBottom: '4px'
                              }}
                            >
                              Container tracking and scheduling.
                            </p>
                            <p
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontWeight: 400,
                                fontSize: '11px',
                                lineHeight: '16px',
                                color: '#69697A'
                              }}
                            >
                              Configure in backend .env: MAERSK_CONSUMER_KEY, MAERSK_BEARER_TOKEN or MAERSK_CONSUMER_SECRET.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Inventory / Stock Recon (Gmail) */}
                  <div>
                    <h3
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 500,
                        fontStyle: 'normal',
                        fontSize: '14px',
                        lineHeight: '22px',
                        letterSpacing: '0px',
                        color: '#69697A',
                        marginBottom: '16px'
                      }}
                    >
                      Inventory – Stock Recon
                    </h3>
                    <div className="space-y-4">
                      <div className="bg-white rounded-lg border" style={{ border: '1px solid #E9E9EA', borderRadius: '8px', padding: '16px' }}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3 flex-1">
                            <div className="flex-shrink-0" style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <img
                                src="/images/Excel-File.svg"
                                alt="Stock Recon"
                                style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4
                                style={{
                                  fontFamily: 'Lexend Deca',
                                  fontWeight: 500,
                                  fontSize: '14px',
                                  lineHeight: '22px',
                                  color: '#212126',
                                  marginBottom: '4px'
                                }}
                              >
                                Gmail – Stock Recon Import
                              </h4>
                              <p
                                style={{
                                  fontFamily: 'Lexend Deca',
                                  fontWeight: 400,
                                  fontSize: '12px',
                                  lineHeight: '16px',
                                  color: '#69697A'
                                }}
                              >
                                Daily Stock Recon Excel emails from ZACPAK are imported automatically at 10:00 SA time. Connect the inventory inbox and use Load Stock Levels to import on demand.
                              </p>
                              {!gmailLoading && gmailStatus?.setupIssues && gmailStatus.setupIssues.length > 0 && (
                                <div className="mt-2 space-y-1">
                                  {gmailStatus.setupIssues.map((issue) => (
                                    <p
                                      key={issue}
                                      style={{
                                        fontFamily: 'Lexend Deca',
                                        fontWeight: 400,
                                        fontSize: '12px',
                                        lineHeight: '16px',
                                        color: '#D97706',
                                      }}
                                    >
                                      {issue}
                                    </p>
                                  ))}
                                </div>
                              )}
                              {!gmailLoading && gmailStatus?.oauthRedirectUri && !gmailStatus.connected && (
                                <p
                                  className="mt-2"
                                  style={{
                                    fontFamily: 'Lexend Deca',
                                    fontWeight: 400,
                                    fontSize: '11px',
                                    lineHeight: '15px',
                                    color: '#69697A',
                                  }}
                                >
                                  Google Cloud redirect URI must include:{' '}
                                  <code style={{ fontSize: '11px' }}>{gmailStatus.oauthRedirectUri}</code>
                                </p>
                              )}
                              {!gmailLoading && gmailStatus?.connected && (
                                <p
                                  className="mt-1"
                                  style={{
                                    fontFamily: 'Lexend Deca',
                                    fontWeight: 500,
                                    fontSize: '12px',
                                    color: '#13AB5B'
                                  }}
                                >
                                  Connected as {gmailStatus.email || 'Gmail'}
                                </p>
                              )}
                              {stockImportResult?.imported && stockImportMessage && (
                                <p
                                  className="mt-2"
                                  style={{
                                    fontFamily: 'Lexend Deca',
                                    fontWeight: 500,
                                    fontSize: '12px',
                                    color: '#13AB5B'
                                  }}
                                >
                                  {stockImportMessage}
                                  {stockImportResult.subject ? ` (${stockImportResult.subject})` : ''}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col flex-wrap items-center gap-2 flex-shrink-0">
                            {gmailLoading ? (
                              <span style={{ fontSize: '12px', color: '#69697A' }}>Checking…</span>
                            ) : gmailStatus?.connected ? (
                              <>
                                <button
                                  type="button"
                                  onClick={handleLoadStockLevels}
                                  disabled={stockImportLoading}
                                  className="px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 disabled:opacity-50"
                                  style={{ fontFamily: 'Lexend Deca', backgroundColor: '#212126' }}
                                >
                                  {stockImportLoading ? 'Loading…' : 'Load Stock Levels'}
                                </button>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    setGmailDisconnectLoading(true)
                                    try {
                                      await disconnectGmail()
                                      toast.success('Gmail disconnected.')
                                      setGmailStatus({ connected: false })
                                      setStockImportResult(null)
                                      setStockImportMessage(null)
                                    } catch (e: any) {
                                      toast.error(e?.response?.data?.error?.message || 'Failed to disconnect Gmail.')
                                    } finally {
                                      setGmailDisconnectLoading(false)
                                    }
                                  }}
                                  disabled={gmailDisconnectLoading || stockImportLoading}
                                  className="px-3 py-1.5 text-xs font-medium rounded-md border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50"
                                  style={{ fontFamily: 'Lexend Deca' }}
                                >
                                  {gmailDisconnectLoading ? 'Disconnecting…' : 'Disconnect Gmail'}
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                onClick={handleConnectGmail}
                                className="px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90"
                                style={{ fontFamily: 'Lexend Deca', backgroundColor: '#212126' }}
                              >
                                Connect Gmail
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Accounting Integration Section */}
                  <div>
                    <h3
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 500,
                        fontStyle: 'normal',
                        fontSize: '14px',
                        lineHeight: '22px',
                        letterSpacing: '0px',
                        color: '#69697A',
                        marginBottom: '16px'
                      }}
                    >
                      Accounting Integration
                    </h3>
                    <div className="space-y-4">
                      {/* Xero */}
                      <div className="bg-white rounded-lg border" style={{ border: '1px solid #E9E9EA', borderRadius: '8px', padding: '16px' }}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3 flex-1">
                            <div className="flex-shrink-0" style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <img 
                                src="/images/xero.svg" 
                                alt="Xero" 
                                style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4
                                style={{
                                  fontFamily: 'Lexend Deca',
                                  fontWeight: 500,
                                  fontSize: '14px',
                                  lineHeight: '22px',
                                  color: '#212126',
                                  marginBottom: '4px'
                                }}
                              >
                                Xero
                              </h4>
                              <p
                                style={{
                                  fontFamily: 'Lexend Deca',
                                  fontWeight: 400,
                                  fontSize: '12px',
                                  lineHeight: '16px',
                                  color: '#69697A'
                                }}
                              >
                                Invoice and payment tracking. Connect to sync purchases, sales, and payments.
                              </p>
                              {!xeroLoading && xeroStatus?.connected && (
                                <p
                                  className="mt-1"
                                  style={{
                                    fontFamily: 'Lexend Deca',
                                    fontWeight: 500,
                                    fontSize: '12px',
                                    color: '#13AB5B'
                                  }}
                                >
                                  Connected as {xeroStatus.tenantName || 'Xero'}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col flex-wrap items-center gap-2 flex-shrink-0">
                            {xeroLoading ? (
                              <span style={{ fontSize: '12px', color: '#69697A' }}>Checking…</span>
                            ) : xeroStatus?.connected ? (
                              <>
                                <button
                                  type="button"
                                  onClick={handleSyncAllXero}
                                  disabled={xeroSyncAllLoading || xeroSyncLoading !== null}
                                  className="px-3 py-1.5 text-xs font-medium rounded-md text-white hover:opacity-90 disabled:opacity-50"
                                  style={{ fontFamily: 'Lexend Deca', backgroundColor: '#212126' }}
                                >
                                  {xeroSyncAllLoading ? 'Syncing all…' : 'Sync all'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleSyncXero('invoices')}
                                  disabled={xeroSyncLoading !== null || xeroSyncAllLoading}
                                  className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                                  style={{ fontFamily: 'Lexend Deca' }}
                                >
                                  {xeroSyncLoading === 'invoices' ? 'Syncing…' : 'Sync Invoices'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleSyncXero('purchases')}
                                  disabled={xeroSyncLoading !== null || xeroSyncAllLoading}
                                  className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                                  style={{ fontFamily: 'Lexend Deca' }}
                                >
                                  {xeroSyncLoading === 'purchases' ? 'Syncing…' : 'Sync Purchases'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleSyncXero('payments')}
                                  disabled={xeroSyncLoading !== null || xeroSyncAllLoading}
                                  className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                                  style={{ fontFamily: 'Lexend Deca' }}
                                >
                                  {xeroSyncLoading === 'payments' ? 'Syncing…' : 'Sync Payments'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleSyncXero('contacts')}
                                  disabled={xeroSyncLoading !== null || xeroSyncAllLoading}
                                  className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                                  style={{ fontFamily: 'Lexend Deca' }}
                                >
                                  {xeroSyncLoading === 'contacts' ? 'Syncing…' : 'Sync Contacts'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleSyncXero('products-services')}
                                  disabled={xeroSyncLoading !== null || xeroSyncAllLoading}
                                  className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                                  style={{ fontFamily: 'Lexend Deca' }}
                                >
                                  {xeroSyncLoading === 'products-services' ? 'Syncing…' : 'Sync Products & Services'}
                                </button>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    setXeroPushLoading(true)
                                    try {
                                      const res = await pushXeroOperationalCosts()
                                      toast.success(res.message || 'Operational costs pushed to Xero.')
                                    } catch (e: any) {
                                      toast.error(e?.response?.data?.error?.message || 'Failed to push operational costs.')
                                    } finally {
                                      setXeroPushLoading(false)
                                    }
                                  }}
                                  disabled={xeroPushLoading}
                                  className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                                  style={{ fontFamily: 'Lexend Deca' }}
                                >
                                  {xeroPushLoading ? 'Pushing…' : 'Push Operational Costs to Xero'}
                                </button>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    setXeroDisconnectLoading(true)
                                    try {
                                      await disconnectXero(xeroStatus?.tenantId)
                                      try { sessionStorage.removeItem('xeroConnected') } catch (_) {}
                                      toast.success('Xero disconnected.')
                                      setXeroStatus({ connected: false })
                                      setXeroEnabled(false)
                                      setXeroSnapshot(null)
                                    } catch (e: any) {
                                      toast.error(e?.response?.data?.error?.message || 'Failed to disconnect Xero.')
                                    } finally {
                                      setXeroDisconnectLoading(false)
                                    }
                                  }}
                                  disabled={xeroDisconnectLoading}
                                  className="px-3 py-1.5 text-xs font-medium rounded-md border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50"
                                  style={{ fontFamily: 'Lexend Deca' }}
                                >
                                  {xeroDisconnectLoading ? 'Disconnecting…' : 'Disconnect Xero'}
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                onClick={handleConnectXero}
                                className="px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90"
                                style={{ fontFamily: 'Lexend Deca', backgroundColor: '#212126' }}
                              >
                                Connect to Xero
                              </button>
                            )}
                          </div>
                        </div>
                        {!xeroLoading && xeroStatus?.connected && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <h5 style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '12px', color: '#69697A', marginBottom: '8px' }}>
                              Business Snapshot
                            </h5>
                            <button
                              type="button"
                              onClick={handleLoadXeroSnapshot}
                              disabled={xeroSnapshotLoading}
                              className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                              style={{ fontFamily: 'Lexend Deca' }}
                            >
                              {xeroSnapshotLoading ? 'Loading…' : 'Load Snapshot'}
                            </button>
                            {xeroSnapshot && (
                              <div className="mt-3 p-3 rounded-md bg-gray-50" style={{ fontFamily: 'Lexend Deca', fontSize: '12px' }}>
                                <p><strong>Organisation:</strong> {xeroSnapshot.organisationName}</p>
                                <p><strong>Tenant:</strong> {xeroSnapshot.tenantName}</p>
                                {xeroSnapshot.contactCounts && (
                                  <p><strong>Contacts:</strong> {xeroSnapshot.contactCounts.customers} customers, {xeroSnapshot.contactCounts.suppliers} suppliers</p>
                                )}
                              </div>
                            )}
                            <h5 className="mt-4 pt-4 border-t border-gray-200" style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '12px', color: '#69697A', marginBottom: '8px' }}>
                              LOMS stock & cost summary
                            </h5>
                            <button
                              type="button"
                              onClick={async () => {
                                setXeroStockSummaryLoading(true)
                                try {
                                  const res = await getXeroStockSummary()
                                  setXeroStockSummary(res)
                                } catch (e: any) {
                                  toast.error(e?.response?.data?.error?.message || 'Failed to load stock summary.')
                                } finally {
                                  setXeroStockSummaryLoading(false)
                                }
                              }}
                              disabled={xeroStockSummaryLoading}
                              className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                              style={{ fontFamily: 'Lexend Deca' }}
                            >
                              {xeroStockSummaryLoading ? 'Loading…' : 'Load Stock & Cost Summary'}
                            </button>
                            {xeroStockSummary && (
                              <div className="mt-3 p-3 rounded-md bg-gray-50" style={{ fontFamily: 'Lexend Deca', fontSize: '12px' }}>
                                <p><strong>As of:</strong> {new Date(xeroStockSummary.asOf).toLocaleString()}</p>
                                <p><strong>Warehouses:</strong> {xeroStockSummary.warehouses.length}</p>
                                <p><strong>Lots:</strong> {xeroStockSummary.lots.length}</p>
                                <p><strong>Unreconciled costs:</strong> {xeroStockSummary.unreconciledCostCount}</p>
                                {xeroStockSummary.totalOperationalCostByStage.length > 0 && (
                                  <p><strong>Cost by stage:</strong> {xeroStockSummary.totalOperationalCostByStage.map((s) => `${s.stage}: ${s.currency} ${s.total.toFixed(2)}`).join('; ')}</p>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="bg-white rounded-lg border" style={{ border: '1px solid #E9E9EA', borderRadius: '8px', padding: '16px' }}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3 flex-1">
                            <div
                              className="flex-shrink-0"
                              style={{
                                width: '40px',
                                height: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#00A1E0',
                                borderRadius: '8px',
                                color: '#fff',
                                fontFamily: 'Lexend Deca',
                                fontWeight: 600,
                                fontSize: '11px',
                              }}
                            >
                              SF
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4
                                style={{
                                  fontFamily: 'Lexend Deca',
                                  fontWeight: 500,
                                  fontSize: '14px',
                                  lineHeight: '22px',
                                  color: '#212126',
                                  marginBottom: '4px',
                                }}
                              >
                                Salesforce
                              </h4>
                              <p
                                style={{
                                  fontFamily: 'Lexend Deca',
                                  fontWeight: 400,
                                  fontSize: '12px',
                                  lineHeight: '16px',
                                  color: '#69697A',
                                }}
                              >
                                Customer accounts and sales orders. Use Sync Orders to bring Salesforce Orders into Connect. Warehouse stock still comes from the ZACPAK Stock Recon.
                              </p>
                              {!salesforceLoading && salesforceStatus?.connected && (
                                <p
                                  className="mt-1"
                                  style={{
                                    fontFamily: 'Lexend Deca',
                                    fontWeight: 500,
                                    fontSize: '12px',
                                    color: '#13AB5B',
                                  }}
                                >
                                  Connected{salesforceStatus.username ? ` as ${salesforceStatus.username}` : ''}
                                </p>
                              )}
                              {!salesforceLoading && salesforceStatus?.oauthRedirectUri && !salesforceStatus.connected && (
                                <p
                                  className="mt-2"
                                  style={{
                                    fontFamily: 'Lexend Deca',
                                    fontWeight: 400,
                                    fontSize: '11px',
                                    color: '#69697A',
                                  }}
                                >
                                  Connected App callback:{' '}
                                  <code style={{ fontSize: '11px' }}>{salesforceStatus.oauthRedirectUri}</code>
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col flex-wrap items-center gap-2 flex-shrink-0">
                            {salesforceLoading ? (
                              <span style={{ fontSize: '12px', color: '#69697A' }}>Checking…</span>
                            ) : salesforceStatus?.connected ? (
                              <>
                                <SalesforceSyncButton
                                  job={salesforceSyncJob}
                                  idleLabel="Sync Orders"
                                  onClick={async () => {
                                    if (salesforceSyncJob.status === 'running') return
                                    try {
                                      await startSalesforceSync()
                                    } catch (e: any) {
                                      toast.error(
                                        e?.message ||
                                          e?.response?.data?.error?.message ||
                                          'Could not start Salesforce Order sync.'
                                      )
                                    }
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={async () => {
                                    setSalesforceDisconnectLoading(true)
                                    try {
                                      await disconnectSalesforce()
                                      toast.success('Salesforce disconnected.')
                                      setSalesforceStatus({
                                        connected: false,
                                        oauthConfigured: salesforceStatus.oauthConfigured,
                                        oauthRedirectUri: salesforceStatus.oauthRedirectUri,
                                      })
                                    } catch (e: any) {
                                      toast.error(e?.response?.data?.error?.message || 'Failed to disconnect Salesforce.')
                                    } finally {
                                      setSalesforceDisconnectLoading(false)
                                    }
                                  }}
                                  disabled={salesforceDisconnectLoading || salesforceSyncJob.status === 'running'}
                                  className="px-3 py-1.5 text-xs font-medium rounded-md border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50"
                                  style={{ fontFamily: 'Lexend Deca' }}
                                >
                                  {salesforceDisconnectLoading ? 'Disconnecting…' : 'Disconnect Salesforce'}
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                onClick={handleConnectSalesforce}
                                className="px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90"
                                style={{ fontFamily: 'Lexend Deca', backgroundColor: '#212126' }}
                              >
                                Connect Salesforce
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="pt-4">
                    <button
                      onClick={() => {
                        // Save other integrations (e.g. Xero is managed via Connect/Disconnect). Shipping lines are configured in backend .env.
                        console.log('Saving integrations...', { xeroEnabled })
                      }}
                      className="w-full sm:w-auto px-6 py-2 text-white font-medium rounded-lg hover:opacity-90 transition-opacity"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        color: '#FFFFFF',
                        backgroundColor: '#212126',
                        borderRadius: '6px',
                        paddingTop: '10px',
                        paddingBottom: '10px',
                        paddingLeft: '24px',
                        paddingRight: '24px',
                        height: '40px'
                      }}
                    >
                      Save Integrations
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      case 'notifications':
        return (
          <div className="p-6">
            <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
              {/* Left Section - Header */}
              <div className="flex-shrink-0" style={{ minWidth: '280px' }}>
                <h2
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 600,
                    fontStyle: 'normal',
                    fontSize: '20px',
                    lineHeight: '32px',
                    letterSpacing: '0%',
                    color: '#212126',
                    marginBottom: '8px'
                  }}
                >
                  Notification Settings
                </h2>
                <p
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '22px',
                    letterSpacing: '0%',
                    color: '#212126'
                  }}
                >
                  Configure how and when you receive notifications.
                </p>
              </div>

              {/* Right Section - Notifications Form */}
              <div className="flex-1">
                <div className="space-y-6">
                  {/* Notification Channels Section */}
                  <div>
                    <h3
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 500,
                        fontStyle: 'normal',
                        fontSize: '14px',
                        lineHeight: '22px',
                        letterSpacing: '0px',
                        color: '#69697A',
                        marginBottom: '16px'
                      }}
                    >
                      Notification Channels
                    </h3>
                    <div className="space-y-4">
                      {/* Email Notifications */}
                      <div className="bg-white rounded-lg" style={{ border: '1px solid #E9E9EA', borderRadius: '8px', padding: '16px' }}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: '22px',
                                color: '#212126',
                                marginBottom: '4px'
                              }}
                            >
                              Email Notifications
                            </h4>
                            <p
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontWeight: 400,
                                fontSize: '12px',
                                lineHeight: '16px',
                                color: '#69697A'
                              }}
                            >
                              Receive updates via email
                            </p>
                          </div>
                          <button
                            onClick={() => setEmailNotifications(!emailNotifications)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                              emailNotifications ? 'bg-gray-700' : 'bg-gray-300'
                            }`}
                            style={{
                              height: '24px',
                              width: '44px',
                              borderRadius: '12px',
                              backgroundColor: emailNotifications ? '#212126' : '#E9E9EA',
                              transition: 'background-color 0.2s ease',
                              flexShrink: 0
                            }}
                          >
                            <span
                              className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                                emailNotifications ? 'translate-x-6' : 'translate-x-1'
                              }`}
                              style={{
                                height: '20px',
                                width: '20px',
                                borderRadius: '50%',
                                backgroundColor: '#FFFFFF',
                                transform: emailNotifications ? 'translateX(20px)' : 'translateX(2px)',
                                transition: 'transform 0.2s ease',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                              }}
                            />
                          </button>
                        </div>
                      </div>

                      {/* SMS Notifications */}
                      <div className="bg-white rounded-lg" style={{ border: '1px solid #E9E9EA', borderRadius: '8px', padding: '16px' }}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: '22px',
                                color: '#212126',
                                marginBottom: '4px'
                              }}
                            >
                              SMS Notifications
                            </h4>
                            <p
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontWeight: 400,
                                fontSize: '12px',
                                lineHeight: '16px',
                                color: '#69697A'
                              }}
                            >
                              Receive urgent updates via SMS
                            </p>
                          </div>
                          <button
                            onClick={() => setSmsNotifications(!smsNotifications)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                              smsNotifications ? 'bg-gray-700' : 'bg-gray-300'
                            }`}
                            style={{
                              height: '24px',
                              width: '44px',
                              borderRadius: '12px',
                              backgroundColor: smsNotifications ? '#212126' : '#E9E9EA',
                              transition: 'background-color 0.2s ease',
                              flexShrink: 0
                            }}
                          >
                            <span
                              className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                                smsNotifications ? 'translate-x-6' : 'translate-x-1'
                              }`}
                              style={{
                                height: '20px',
                                width: '20px',
                                borderRadius: '50%',
                                backgroundColor: '#FFFFFF',
                                transform: smsNotifications ? 'translateX(20px)' : 'translateX(2px)',
                                transition: 'transform 0.2s ease',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                              }}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Notification Types Section */}
                  <div>
                    <h3
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 500,
                        fontStyle: 'normal',
                        fontSize: '14px',
                        lineHeight: '22px',
                        letterSpacing: '0px',
                        color: '#69697A',
                        marginBottom: '16px'
                      }}
                    >
                      Notification Types
                    </h3>
                    <div className="space-y-4">
                      {/* Shipment Status Changes */}
                      <div className="bg-white rounded-lg border" style={{ border: '1px solid #E9E9EA', borderRadius: '8px', padding: '16px' }}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: '22px',
                                color: '#212126',
                                marginBottom: '4px'
                              }}
                            >
                              Shipment Status Changes
                            </h4>
                            <p
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontWeight: 400,
                                fontSize: '12px',
                                lineHeight: '16px',
                                color: '#69697A'
                              }}
                            >
                              Updates when shipment status changes
                            </p>
                          </div>
                          <button
                            onClick={() => setShipmentStatusChanges(!shipmentStatusChanges)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                              shipmentStatusChanges ? 'bg-gray-700' : 'bg-gray-300'
                            }`}
                            style={{
                              height: '24px',
                              width: '44px',
                              borderRadius: '12px',
                              backgroundColor: shipmentStatusChanges ? '#212126' : '#E9E9EA',
                              transition: 'background-color 0.2s ease',
                              flexShrink: 0
                            }}
                          >
                            <span
                              className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                                shipmentStatusChanges ? 'translate-x-6' : 'translate-x-1'
                              }`}
                              style={{
                                height: '20px',
                                width: '20px',
                                borderRadius: '50%',
                                backgroundColor: '#FFFFFF',
                                transform: shipmentStatusChanges ? 'translateX(20px)' : 'translateX(2px)',
                                transition: 'transform 0.2s ease',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                              }}
                            />
                          </button>
                        </div>
                      </div>

                      {/* Document Reminders */}
                      <div className="bg-white rounded-lg border" style={{ border: '1px solid #E9E9EA', borderRadius: '8px', padding: '16px' }}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: '22px',
                                color: '#212126',
                                marginBottom: '4px'
                              }}
                            >
                              Document Reminders
                            </h4>
                            <p
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontWeight: 400,
                                fontSize: '12px',
                                lineHeight: '16px',
                                color: '#69697A'
                              }}
                            >
                              Reminders for document submission deadlines
                            </p>
                          </div>
                          <button
                            onClick={() => setDocumentReminders(!documentReminders)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                              documentReminders ? 'bg-gray-700' : 'bg-gray-300'
                            }`}
                            style={{
                              height: '24px',
                              width: '44px',
                              borderRadius: '12px',
                              backgroundColor: documentReminders ? '#212126' : '#E9E9EA',
                              transition: 'background-color 0.2s ease',
                              flexShrink: 0
                            }}
                          >
                            <span
                              className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                                documentReminders ? 'translate-x-6' : 'translate-x-1'
                              }`}
                              style={{
                                height: '20px',
                                width: '20px',
                                borderRadius: '50%',
                                backgroundColor: '#FFFFFF',
                                transform: documentReminders ? 'translateX(20px)' : 'translateX(2px)',
                                transition: 'transform 0.2s ease',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                              }}
                            />
                          </button>
                        </div>
                      </div>

                      {/* Inventory Alerts */}
                      <div className="bg-white rounded-lg border" style={{ border: '1px solid #E9E9EA', borderRadius: '8px', padding: '16px' }}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: '22px',
                                color: '#212126',
                                marginBottom: '4px'
                              }}
                            >
                              Inventory Alerts
                            </h4>
                            <p
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontWeight: 400,
                                fontSize: '12px',
                                lineHeight: '16px',
                                color: '#69697A'
                              }}
                            >
                              Alerts when inventory falls below threshold
                            </p>
                          </div>
                          <button
                            onClick={() => setInventoryAlerts(!inventoryAlerts)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                              inventoryAlerts ? 'bg-gray-700' : 'bg-gray-300'
                            }`}
                            style={{
                              height: '24px',
                              width: '44px',
                              borderRadius: '12px',
                              backgroundColor: inventoryAlerts ? '#212126' : '#E9E9EA',
                              transition: 'background-color 0.2s ease',
                              flexShrink: 0
                            }}
                          >
                            <span
                              className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                                inventoryAlerts ? 'translate-x-6' : 'translate-x-1'
                              }`}
                              style={{
                                height: '20px',
                                width: '20px',
                                borderRadius: '50%',
                                backgroundColor: '#FFFFFF',
                                transform: inventoryAlerts ? 'translateX(20px)' : 'translateX(2px)',
                                transition: 'transform 0.2s ease',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                              }}
                            />
                          </button>
                        </div>
                      </div>

                      {/* Payment Notifications */}
                      <div className="bg-white rounded-lg border" style={{ border: '1px solid #E9E9EA', borderRadius: '8px', padding: '16px' }}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: '22px',
                                color: '#212126',
                                marginBottom: '4px'
                              }}
                            >
                              Payment Notifications
                            </h4>
                            <p
                              style={{
                                fontFamily: 'Lexend Deca',
                                fontWeight: 400,
                                fontSize: '12px',
                                lineHeight: '16px',
                                color: '#69697A'
                              }}
                            >
                              Updates on payment status
                            </p>
                          </div>
                          <button
                            onClick={() => setPaymentNotifications(!paymentNotifications)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                              paymentNotifications ? 'bg-gray-700' : 'bg-gray-300'
                            }`}
                            style={{
                              height: '24px',
                              width: '44px',
                              borderRadius: '12px',
                              backgroundColor: paymentNotifications ? '#212126' : '#E9E9EA',
                              transition: 'background-color 0.2s ease',
                              flexShrink: 0
                            }}
                          >
                            <span
                              className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                                paymentNotifications ? 'translate-x-6' : 'translate-x-1'
                              }`}
                              style={{
                                height: '20px',
                                width: '20px',
                                borderRadius: '50%',
                                backgroundColor: '#FFFFFF',
                                transform: paymentNotifications ? 'translateX(20px)' : 'translateX(2px)',
                                transition: 'transform 0.2s ease',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                              }}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="pt-4">
                    <button
                      onClick={handleSaveNotificationSettings}
                      disabled={savingNotifications}
                      className="w-full sm:w-auto px-6 py-2 text-white font-medium rounded-lg hover:opacity-90 transition-opacity"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        color: '#FFFFFF',
                        backgroundColor: '#212126',
                        borderRadius: '6px',
                        paddingTop: '10px',
                        paddingBottom: '10px',
                        paddingLeft: '24px',
                        paddingRight: '24px',
                        height: '40px',
                        opacity: savingNotifications ? 0.7 : 1,
                        cursor: savingNotifications ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {savingNotifications ? 'Saving...' : 'Save Notification Settings'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      case 'user-management':
        return (
          <div className="p-6">
            <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
              <div className="flex-shrink-0" style={{ minWidth: '280px' }}>
                <h2 style={{ fontFamily: 'Lexend Deca', fontWeight: 600, fontSize: '20px', lineHeight: '32px', color: '#212126', marginBottom: '8px' }}>
                  User Management
                </h2>
                <p style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '22px', color: '#212126' }}>
                  Manage user accounts and permissions
                </p>
              </div>
              <div className="flex-1">
                <div className="space-y-6">
                  {!currentUser && (
                    <p style={{ fontFamily: 'Lexend Deca', color: '#69697A' }}>Sign in to view user management.</p>
                  )}
                  {currentUser && !isAdmin && (
                    <div>
                      <h3 style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', marginBottom: '12px' }}>
                        Your profile
                      </h3>
                      <p style={{ fontFamily: 'Lexend Deca', fontSize: '12px', color: '#69697A', marginBottom: '12px' }}>
                        Only administrators can view all users. You can edit your own profile below.
                      </p>
                      <div
                        className="bg-white rounded-lg"
                        style={{ border: '1px solid #E9E9EA', borderRadius: '8px', padding: '16px', maxWidth: '480px' }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-1">
                            <div
                              className="flex-shrink-0 w-10 h-10 rounded-full bg-[#E9F3FD] flex items-center justify-center overflow-hidden"
                            >
                              {getAvatarUrl(currentUser.avatar) ? (
                                <img src={getAvatarUrl(currentUser.avatar)!} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <span style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', color: '#1F87E8' }}>
                                  {(currentUser.name || currentUser.email).split(/\s+/).map((s) => s[0]).join('').toUpperCase().slice(0, 2) || '?'}
                                </span>
                              )}
                            </div>
                            <div>
                              <h4 style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#212126', margin: 0 }}>
                                {currentUser.name}
                              </h4>
                              <p style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '16px', color: '#69697A', margin: 0, marginTop: '2px' }}>
                                {currentUser.email} · {ROLE_LABELS[currentUser.role as UserRole] || currentUser.role}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingUser({ ...currentUser, role: currentUser.role as UserRole, createdAt: '', updatedAt: '' })
                              setUserForm({ name: currentUser.name, email: currentUser.email, password: '', role: currentUser.role as UserRole })
                              setEditUserOpen(true)
                            }}
                            className="font-medium rounded-lg hover:opacity-90"
                            style={{
                              fontFamily: 'Lexend Deca', fontWeight: 600, fontSize: '12px', color: '#212126',
                              backgroundColor: '#FFFFFF', border: '1px solid #212126', borderRadius: '6px',
                              padding: '6px 12px', height: '32px'
                            }}
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {currentUser && isAdmin && (
                    <>
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <h3 style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A' }}>
                            Active Users
                          </h3>
                          <button
                            type="button"
                            onClick={() => {
                              setUserForm({ name: '', email: '', password: '', role: 'operations_manager' })
                              setEditingUser(null)
                              setAddUserOpen(true)
                            }}
                            className="flex items-center gap-2 text-white font-medium rounded-lg hover:opacity-90 transition-opacity"
                            style={{
                              fontFamily: 'Lexend Deca', fontSize: '14px', backgroundColor: '#212126', borderRadius: '6px',
                              padding: '8px 16px', height: '36px'
                            }}
                          >
                            <FiPlus className="w-4 h-4" />
                            Add User
                          </button>
                        </div>
                        {userManagementError && (
                          <p className="mb-3" style={{ fontFamily: 'Lexend Deca', fontSize: '12px', color: '#dc2626' }}>{userManagementError}</p>
                        )}
                        {usersLoading ? (
                          <p style={{ fontFamily: 'Lexend Deca', color: '#69697A' }}>Loading users…</p>
                        ) : (
                          <div className="space-y-3">
                            {usersList.map((user) => {
                              const initials = (user.name || user.email).split(/\s+/).map((s) => s[0]).join('').toUpperCase().slice(0, 2) || '?'
                              const isCurrent = currentUser?.id === user.id
                              return (
                                <div
                                  key={user.id}
                                  className="bg-white rounded-lg"
                                  style={{ border: '1px solid #E9E9EA', borderRadius: '8px', padding: '16px' }}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3 flex-1">
                                      <div
                                        style={{
                                          width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#E9F3FD',
                                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                        }}
                                      >
                                        <span style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', color: '#1F87E8' }}>{initials}</span>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <h4 style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#212126', margin: 0 }}>
                                            {user.name}
                                          </h4>
                                          {isCurrent && (
                                            <>
                                              <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#69697A', display: 'inline-block' }} />
                                              <span style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', color: '#69697A' }}>You</span>
                                            </>
                                          )}
                                        </div>
                                        <p style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '16px', color: '#69697A', margin: 0, marginTop: '2px' }}>
                                          {user.email} · {ROLE_LABELS[user.role as UserRole]}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setEditingUser(user)
                                          setUserForm({ name: user.name, email: user.email, password: '', role: user.role as UserRole })
                                          setEditUserOpen(true)
                                        }}
                                        className="font-medium rounded-lg hover:opacity-90"
                                        style={{
                                          fontFamily: 'Lexend Deca', fontWeight: 600, fontSize: '12px', color: '#212126',
                                          backgroundColor: '#FFFFFF', border: '1px solid #212126', borderRadius: '6px',
                                          padding: '6px 12px', height: '32px'
                                        }}
                                      >
                                        Edit
                                      </button>
                                      {isAdmin && !isCurrent && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            if (window.confirm(`Delete user ${user.name} (${user.email})?`)) {
                                              deleteUser(user.id)
                                                .then(() => {
                                                  setUsersList((prev) => prev.filter((u) => u.id !== user.id))
                                                  toast.success('User deleted.')
                                                })
                                                .catch((e: any) => toast.error(e?.response?.data?.error?.message ?? e?.message ?? 'Failed to delete user'))
                                            }
                                          }}
                                          className="font-medium rounded-lg hover:opacity-90"
                                          style={{
                                            fontFamily: 'Lexend Deca', fontSize: '12px', color: '#dc2626',
                                            backgroundColor: '#FFFFFF', border: '1px solid #dc2626', borderRadius: '6px',
                                            padding: '6px 12px', height: '32px'
                                          }}
                                        >
                                          Delete
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                      <div>
                        <h3 style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#69697A', marginBottom: '16px' }}>
                          Role Permissions
                        </h3>
                        <div className="space-y-3">
                          {rolesList.map((role) => (
                            <div
                              key={role.id}
                              className="bg-white rounded-lg"
                              style={{ border: '1px solid #E9E9EA', borderRadius: '8px', padding: '16px' }}
                            >
                              <div className="flex-1">
                                <h4 style={{ fontFamily: 'Lexend Deca', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#212126', marginBottom: '4px' }}>
                                  {role.title}
                                </h4>
                                <p style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '16px', color: '#69697A', margin: 0 }}>
                                  {role.description}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Add User Modal */}
            {addUserOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setAddUserOpen(false)}>
                <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
                  <h3 style={{ fontFamily: 'Lexend Deca', fontWeight: 600, fontSize: '18px', marginBottom: '16px' }}>Add User</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: 'Lexend Deca' }}>Name</label>
                      <input
                        type="text"
                        value={userForm.name}
                        onChange={(e) => setUserForm((f) => ({ ...f, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Full name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: 'Lexend Deca' }}>Email</label>
                      <input
                        type="email"
                        value={userForm.email}
                        onChange={(e) => setUserForm((f) => ({ ...f, email: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="email@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: 'Lexend Deca' }}>Password (min 6 characters)</label>
                      <input
                        type="password"
                        value={userForm.password}
                        onChange={(e) => setUserForm((f) => ({ ...f, password: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="••••••••"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: 'Lexend Deca' }}>Role</label>
                      <select
                        value={userForm.role}
                        onChange={(e) => setUserForm((f) => ({ ...f, role: e.target.value as UserRole }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        {(Object.keys(ROLE_LABELS) as UserRole[]).map((r) => (
                          <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="mt-6 flex gap-2 justify-end">
                    <button type="button" onClick={() => setAddUserOpen(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" style={{ fontFamily: 'Lexend Deca' }}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={userFormSubmitting || !userForm.name.trim() || !userForm.email.trim() || userForm.password.length < 6}
                      onClick={() => {
                        setUserFormSubmitting(true)
                        const payload: CreateUserPayload = { name: userForm.name.trim(), email: userForm.email.trim(), password: userForm.password, role: userForm.role }
                        createUser(payload)
                          .then((newUser) => {
                            setUsersList((prev) => [newUser, ...prev])
                            setAddUserOpen(false)
                            setUserForm({ name: '', email: '', password: '', role: 'operations_manager' })
                            toast.success('User created.')
                          })
                          .catch((e: any) => toast.error(e?.response?.data?.error?.message ?? e?.message ?? 'Failed to create user'))
                          .finally(() => setUserFormSubmitting(false))
                      }}
                      className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                      style={{ fontFamily: 'Lexend Deca' }}
                    >
                      {userFormSubmitting ? 'Creating…' : 'Create User'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Edit User Modal */}
            {editUserOpen && editingUser && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => { setEditUserOpen(false); setEditingUser(null) }}>
                <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
                  <h3 style={{ fontFamily: 'Lexend Deca', fontWeight: 600, fontSize: '18px', marginBottom: '16px' }}>Edit User</h3>
                  <div className="space-y-4">
                    {/* Avatar */}
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {getAvatarUrl(editingUser.avatar) ? (
                          <img src={getAvatarUrl(editingUser.avatar)!} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-lg font-medium text-gray-600">
                            {(editingUser.name || editingUser.email).split(/\s+/).map((s) => s[0]).join('').toUpperCase().slice(0, 2) || '?'}
                          </span>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: 'Lexend Deca' }}>Profile photo</label>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          className="hidden"
                          id="edit-user-avatar-input"
                          onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (!file) return
                            setAvatarUploading(true)
                            // In Okta mode, localStorage user.id can be Okta subject (e.g. 00u...),
                            // while backend avatar route expects our local DB user UUID.
                            // Resolve self user ID via /users/me before upload.
                            getCurrentUserApi()
                              .then((me) => {
                                const targetUserId = me.email === editingUser.email ? me.id : editingUser.id
                                return uploadUserAvatar(targetUserId, file)
                              })
                              .then((updated) => {
                                setEditingUser(updated)
                                setUsersList((prev) => prev.map((u) => (u.id === updated.id ? updated : u)))
                                if (isCurrentUserRecord(updated)) setUser(updated)
                                toast.success('Avatar updated.')
                              })
                              .catch((err: any) => toast.error(err?.response?.data?.error?.message ?? err?.message ?? 'Failed to upload avatar'))
                              .finally(() => {
                                setAvatarUploading(false)
                                e.target.value = ''
                              })
                          }}
                        />
                        <label
                          htmlFor="edit-user-avatar-input"
                          className={`inline-block px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 cursor-pointer ${avatarUploading ? 'opacity-60 pointer-events-none' : 'hover:bg-gray-50'}`}
                          style={{ fontFamily: 'Lexend Deca' }}
                        >
                          {avatarUploading ? 'Uploading…' : 'Upload photo'}
                        </label>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: 'Lexend Deca' }}>Name</label>
                      <input
                        type="text"
                        value={userForm.name}
                        onChange={(e) => setUserForm((f) => ({ ...f, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Full name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: 'Lexend Deca' }}>Email</label>
                      <input
                        type="email"
                        value={userForm.email}
                        onChange={(e) => setUserForm((f) => ({ ...f, email: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="email@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: 'Lexend Deca' }}>New password (leave blank to keep current)</label>
                      <input
                        type="password"
                        value={userForm.password}
                        onChange={(e) => setUserForm((f) => ({ ...f, password: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="••••••••"
                      />
                    </div>
                    {isAdmin && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: 'Lexend Deca' }}>Role</label>
                        <select
                          value={userForm.role}
                          onChange={(e) => setUserForm((f) => ({ ...f, role: e.target.value as UserRole }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        >
                          {(Object.keys(ROLE_LABELS) as UserRole[]).map((r) => (
                            <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                  <div className="mt-6 flex gap-2 justify-end">
                    <button type="button" onClick={() => { setEditUserOpen(false); setEditingUser(null) }} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" style={{ fontFamily: 'Lexend Deca' }}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={userFormSubmitting || !userForm.name.trim() || !userForm.email.trim() || (userForm.password.length > 0 && userForm.password.length < 6)}
                      onClick={() => {
                        setUserFormSubmitting(true)
                        const payload: UpdateUserPayload = {
                          name: userForm.name.trim(),
                          email: userForm.email.trim(),
                        }
                        if (isAdmin) payload.role = userForm.role
                        if (userForm.password.length >= 6) payload.password = userForm.password
                        updateUser(editingUser.id, payload)
                          .then((updated) => {
                            setUsersList((prev) => prev.map((u) => (u.id === updated.id ? updated : u)))
                            setEditUserOpen(false)
                            setEditingUser(null)
                            if (isCurrentUserRecord(updated)) {
                              setUser(updated)
                            }
                            toast.success('User updated.')
                          })
                          .catch((e: any) => toast.error(e?.response?.data?.error?.message ?? e?.message ?? 'Failed to update user'))
                          .finally(() => setUserFormSubmitting(false))
                      }}
                      className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                      style={{ fontFamily: 'Lexend Deca' }}
                    >
                      {userFormSubmitting ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )
      default:
        return <div className="p-6 text-gray-500">General settings content will be developed here.</div>
    }
  }

  return (
    <div className="space-y-6" style={{ maxWidth: '100%' }}>
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <h1 
          style={{
            fontFamily: 'Lexend Deca',
            fontWeight: 600,
            fontStyle: 'normal',
            fontSize: '20px',
            lineHeight: '32px',
            letterSpacing: '0%',
            color: '#212126'
          }}
        >
          Settings
        </h1>
        <div>
          <span
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 400,
              fontStyle: 'normal',
              fontSize: '12px',
              lineHeight: '28px',
              letterSpacing: '0%',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <span style={{ color: '#69697A' }}>Home</span>
            <span style={{ color: '#69697A', margin: '0 4px' }}> &gt; </span>
            <span style={{ color: '#212126' }}>Settings</span>
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div 
        className="flex items-center"
        style={{
          width: '550px',
          height: '38px',
          borderRadius: '8px',
          border: '1px solid #E9E9EA',
          backgroundColor: '#FFFFFF',
          padding: '4px',
          gap: '0px'
        }}
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.key
          const path = tabToPath[tab.key]
          return (
            <Link
              key={tab.key}
              to={path}
              onClick={() => handleTabClick(tab.key)}
              style={{
                flex: 1,
                height: '30px',
                borderRadius: '6px',
                padding: '6px 8px',
                backgroundColor: isActive ? '#212126' : 'transparent',
                color: isActive ? '#FFFFFF' : '#69697A',
                fontFamily: 'Lexend Deca',
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: '14px',
                lineHeight: '20px',
                letterSpacing: '0%',
                border: 'none',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                wordSpacing: '0px',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textDecoration: 'none'
              }}
            >
              {tab.label}
            </Link>
          )
        })}
      </div>

      {/* Tab Content */}
      <div style={{ minHeight: '400px' }}>
        {getTabContent()}
      </div>
    </div>
  )
}

export default Settings

